import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { resetPasswordApi } from '@/lib/api'
import { BRAND_MINT } from '@/lib/utils'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  EyeIcon,
  EyeOffIcon,
  KeyIcon,
  ShieldCheckIcon,
  AlertCircleIcon,
} from 'lucide-react'

export const Route = createFileRoute('/reset-password')({
  validateSearch: (search: Record<string, string | undefined>): { token?: string } => ({
    token: search.token,
  }),
  component: () => {
    const { token } = Route.useSearch()
    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)

    const successHeadingRef = useRef<HTMLHeadingElement>(null)

    useEffect(() => {
      if (submitted && successHeadingRef.current) {
        successHeadingRef.current.focus()
      }
    }, [submitted])

    // Validate token presence
    if (!token) {
      return (
        <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
          <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />

          <div className="relative z-10 flex items-center justify-center pt-12 sm:pt-16">
            <Link to="/" className="flex items-center gap-2.5">
              <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
                PP
              </div>
              <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
            </Link>
          </div>

          <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16">
            <div className="w-full max-w-md">
              <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50 text-center">
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                  style={{ backgroundColor: BRAND_MINT }}
                />
                <div className="flex flex-col items-center">
                  <div className="flex size-14 items-center justify-center rounded-full bg-red-500/15">
                    <AlertCircleIcon className="size-7 text-red-400" />
                  </div>
                  <h1 className="mt-5 font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-medium tracking-tight text-white">
                    Invalid reset link
                  </h1>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xs">
                    This password reset link is missing or invalid. Please request a new one.
                  </p>
                  <Link
                    to="/forgot-password"
                    className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 transition-colors"
                  >
                    <ArrowLeftIcon className="size-4" />
                    Request new reset link
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault()

      if (!password || password.length < 8) {
        toast.error('Password must be at least 8 characters')
        return
      }

      if (password !== confirmPassword) {
        toast.error('Passwords do not match')
        return
      }

      setLoading(true)
      try {
        await resetPasswordApi(token, password)
        setSubmitted(true)
        toast.success('Password reset successfully!')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to reset password'
        toast.error(message)
      } finally {
        setLoading(false)
      }
    }

    return (
      <div className="relative flex min-h-svh flex-col bg-[#0B1120] overflow-hidden">
        {/* Subtle grid pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />

        {/* Decorative glow orbs */}
        <div className="pointer-events-none absolute -top-40 -right-40 size-[500px] rounded-full bg-[#00E5A0]/10 blur-[120px]" />
        <div className="pointer-events-none absolute -bottom-40 -left-40 size-[400px] rounded-full bg-[#00E5A0]/5 blur-[100px]" />
        <div className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] rounded-full bg-[#00E5A0]/5 blur-[80px]" />

        {/* Brand lockup */}
        <div className="relative z-10 flex items-center justify-center pt-12 sm:pt-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex size-9 items-center justify-center rounded-xl bg-[#00E5A0] text-[#0B1120] text-sm font-bold shadow-lg shadow-[#00E5A0]/30">
              PP
            </div>
            <span className="text-lg font-semibold tracking-tight text-white">PeerPass</span>
          </Link>
        </div>

        {/* Reset password form */}
        <div className="relative z-10 flex flex-1 items-center justify-center px-6 pb-12 sm:pb-16">
          <div className="w-full max-w-md">
            <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-8 sm:p-10 shadow-2xl shadow-black/50">
              {/* Mint accent bar */}
              <span
                aria-hidden="true"
                className="absolute top-0 left-8 right-8 h-[2px] rounded-full"
                style={{ backgroundColor: BRAND_MINT }}
              />

              {submitted ? (
                /* Success state */
                <div className="flex flex-col items-center text-center">
                  <div className="flex size-14 items-center justify-center rounded-full bg-[#00E5A0]/15">
                    <ShieldCheckIcon className="size-7 text-[#00E5A0]" />
                  </div>
                  <h1
                    ref={successHeadingRef}
                    tabIndex={-1}
                    className="mt-5 font-['Source_Serif_4',ui-serif,Georgia,serif] text-2xl font-medium tracking-tight text-white outline-none"
                  >
                    Password reset successfully
                  </h1>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed max-w-xs">
                    Your password has been updated. You can now log in with your new password.
                  </p>
                  <Link
                    to="/login"
                    className="mt-8 inline-flex items-center gap-2 rounded-lg bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base px-6 py-3 shadow-xl shadow-[#00E5A0]/25 transition-all"
                  >
                    Log in with new password
                    <ArrowRightIcon className="size-4" />
                  </Link>
                </div>
              ) : (
                <>
                  <div className="flex flex-col gap-2 text-center mb-8">
                    <h1 className="font-['Source_Serif_4',ui-serif,Georgia,serif] text-3xl font-medium tracking-tight text-white">
                      Set new password
                    </h1>
                    <p className="text-sm text-white/60">
                      Choose a strong password for your account.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-5" aria-label="Set new password">
                    {/* New password */}
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="new-password" className="text-sm font-medium text-white/70">
                        New password
                      </Label>
                      <div className="relative">
                        <Input
                          id="new-password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="At least 8 characters"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={8}
                          autoComplete="new-password"
                          autoFocus
                          className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                          tabIndex={-1}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm password */}
                    <div className="flex flex-col gap-2">
                      <Label htmlFor="confirm-password" className="text-sm font-medium text-white/70">
                        Confirm password
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirm-password"
                          type={showConfirm ? 'text' : 'password'}
                          placeholder="Repeat your password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          autoComplete="new-password"
                          className="border-white/15 bg-white/[0.04] text-white placeholder:text-white/30 focus-visible:border-[#00E5A0] focus-visible:ring-[#00E5A0]/30 h-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm((v) => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors"
                          tabIndex={-1}
                          aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
                        >
                          {showConfirm ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Password hint */}
                    <div className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-3">
                      <KeyIcon className="size-4 text-[#00E5A0] mt-0.5 shrink-0" />
                      <p className="text-xs text-white/40 leading-relaxed">
                        Make it at least 8 characters. Use a mix of letters, numbers, and symbols for a stronger password.
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#00E5A0] text-[#0B1120] hover:bg-[#00E5A0]/90 font-semibold text-base py-6 mt-2 shadow-xl shadow-[#00E5A0]/25 disabled:opacity-60"
                      disabled={loading}
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <span className="size-4 rounded-full border-2 border-[#0B1120]/30 border-t-[#0B1120] animate-spin" />
                          Resetting…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          Reset password
                          <CheckCircleIcon className="size-4" />
                        </span>
                      )}
                    </Button>
                  </form>

                  <p className="mt-6 text-center text-sm text-white/40">
                    <Link
                      to="/login"
                      className="text-[#00E5A0] font-medium hover:text-[#00E5A0]/80 underline underline-offset-4 transition-colors"
                    >
                      Back to log in
                    </Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Bottom trust bar */}
        <div className="relative z-10 flex justify-center pb-8">
          <div className="flex items-center gap-4 text-xs text-white/30">
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              Employer-paid benefit
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              0% interest
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircleIcon className="size-3 text-[#00E5A0]/60" />
              100% anonymous
            </span>
          </div>
        </div>
      </div>
    )
  },
})
